import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLanguage } from "@fortawesome/pro-light-svg-icons";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { fetchApi } from "../../../helpers/reactQueryApi";

/**
 * Props for the TranslationPopover component
 */
interface TranslationPopoverProps {
  /** The field name being translated (e.g., "text", "title", "description") */
  field: string;

  /**
   * The current translations object
   * Format: { "en": { "fieldName": "value" }, "de": { "fieldName": "value" } }
   */
  translationsValue: Record<string, Record<string, string>>;

  /**
   * Callback invoked when the user saves translations
   * Receives the updated translations object with all languages
   */
  onTranslationsChange: (
    translations: Record<string, Record<string, string>>,
  ) => void;

  /** Callback to close the popover */
  onClose: () => void;

  /** Whether to render textareas instead of text inputs (for multiline content) */
  multiline?: boolean;

  /** Number of rows for textarea when multiline is true */
  rows?: number;

  /** Maximum character length for each translation input/textarea */
  maxLength?: number;

  /**
   * Custom input renderer. When provided, replaces the default text input/textarea
   * for each language (e.g. to plug in a rich-text/markdown editor). The default
   * fallback is the built-in input/textarea when this is omitted.
   */
  renderInput?: (args: {
    language: string;
    value: string;
    onChange: (value: string) => void;
    isDefault: boolean;
    maxLength?: number;
  }) => React.ReactNode;

  /** Render the popover wider (for rich editors that need more room). */
  wide?: boolean;
}

const getLanguageName = (code: string): string => {
  return I18n.t(`js.networks.languages.${code}`);
};

/**
 * Popover content component for editing translations across multiple languages
 *
 * Features:
 * - Displays text input fields for all configured languages
 * - Marks the network's default language with "(Standardsprache)" label
 * - Manages local editing state until the user clicks Save
 * - No API calls - works purely with controlled component pattern
 * - Used internally by TranslatableTextInput
 *
 * Behavior:
 * - Initializes input fields from translationsValue on mount
 * - Changes are kept in local state until Save is clicked
 * - Cancel discards all changes and closes the popover
 * - Save calls onTranslationsChange with updated translations and closes popover
 *
 * @example
 * ```tsx
 * <TranslationPopover
 *   field="text"
 *   translationsValue={item.translations}
 *   onTranslationsChange={(translations) => {
 *     setItem({ ...item, translations });
 *   }}
 *   onClose={() => setPopoverOpen(false)}
 * />
 * ```
 */
export default function TranslationPopover({
  field,
  translationsValue,
  onTranslationsChange,
  onClose,
  multiline = false,
  rows = 4,
  maxLength,
  renderInput,
  wide = false,
}: TranslationPopoverProps) {
  const currentLocale = Preload.current_network?.language.split("_")[0];
  const deepLEnabled =
    Preload.current_network?.config?.translations?.deep_l?.enabled;
  // Get available languages from network config
  const availableLanguages =
    Preload.current_network?.config.multilingualism.available_languages;

  // Draft (language -> value), seeded once on mount: forms re-render with a
  // fresh translations object at any time, which must not wipe typed input.
  const [editedTranslations, setEditedTranslations] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      availableLanguages.map((lang) => [
        lang,
        translationsValue[lang]?.[field] || "",
      ]),
    ),
  );

  // Track translation loading state
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslationChange = (language: string, value: string) => {
    setEditedTranslations((prev) => ({
      ...prev,
      [language]: value,
    }));
  };

  const handleSave = () => {
    // Build the updated translations object, pruning blanks so the
    // translations hash doesn't accumulate empty placeholder entries.
    // - Non-blank values are written as before.
    // - Explicit clear (key previously existed, now empty): delete the field,
    //   and drop the per-locale sub-hash entirely if it becomes empty. The
    //   Translatable fallback chain then naturally picks the base value.
    // - Never-filled + still empty: skip (don't create a key).
    const updatedTranslations: Record<string, Record<string, string>> = {
      ...translationsValue,
    };

    Object.entries(editedTranslations).forEach(([lang, value]) => {
      if (value) {
        updatedTranslations[lang] = {
          ...(updatedTranslations[lang] || {}),
          [field]: value,
        };
      } else if (updatedTranslations[lang]?.[field] !== undefined) {
        const next = { ...updatedTranslations[lang] };
        delete next[field];
        if (Object.keys(next).length === 0) {
          delete updatedTranslations[lang];
        } else {
          updatedTranslations[lang] = next;
        }
      }
    });

    onTranslationsChange(updatedTranslations);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleTranslateWithDeepL = () => {
    const sourceText = editedTranslations[currentLocale];

    if (!sourceText || sourceText.trim() === "") {
      toastr.error(
        I18n.t("js.shared.multilingualism.translation_popover.no_source_text"),
      );
      return;
    }

    // Find languages with empty translations
    const emptyLanguages = availableLanguages.filter(
      (lang) =>
        lang !== currentLocale &&
        (!editedTranslations[lang] || editedTranslations[lang].trim() === ""),
    );

    if (emptyLanguages.length === 0) {
      toastr.info(
        I18n.t("js.shared.multilingualism.translation_popover.all_filled"),
      );
      return;
    }

    setIsTranslating(true);
    let completedTranslations = 0;
    const totalTranslations = emptyLanguages.length;

    emptyLanguages.forEach((language) => {
      fetchApi(`/translations/${language}/raw`, {
        method: "POST",
        body: { text: sourceText },
      })
        .then((data) => {
          // Update editedTranslations with the translated text
          setEditedTranslations((prev) => ({
            ...prev,
            [language]: data.text || "",
          }));

          completedTranslations++;
          if (completedTranslations === totalTranslations) {
            setIsTranslating(false);
            toastr.success(
              I18n.t(
                "js.shared.multilingualism.translation_popover.translation_success",
              ),
            );
          }
        })
        .catch((e) => {
          console.error(e);
          completedTranslations++;
          if (completedTranslations === totalTranslations) {
            setIsTranslating(false);
          }
          toastr.error(
            I18n.t("js.shared.multilingualism.translation_popover.error"),
          );
        });
    });
  };

  return (
    <div
      className="translation-popover-content"
      data-testid="translation-popover"
      style={{ width: wide ? "720px" : "400px", maxWidth: "90vw" }}
    >
      <div className="mb-2 flex items-baseline gap-2 justify-between">
        <h3>
          {I18n.t("js.shared.multilingualism.translation_popover.heading")}
        </h3>
        {deepLEnabled && (
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={handleTranslateWithDeepL}
            disabled={isTranslating}
          >
            {isTranslating ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                {I18n.t(
                  "js.shared.multilingualism.translation_popover.translating",
                )}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLanguage} className="me-2" />
                {I18n.t(
                  "js.shared.multilingualism.translation_popover.translate_with_deepl",
                )}
              </>
            )}
          </button>
        )}
      </div>

      <div className="mb-3">
        {availableLanguages.map((language) => (
          <div key={language} className="mb-2">
            <label className="form-label mb-1 flex gap-1 items-baseline">
              {getLanguageName(language)}
              {language === currentLocale && (
                <span className="text-sm">
                  (
                  {I18n.t(
                    "js.shared.multilingualism.translation_popover.default_language",
                  )}
                  )
                </span>
              )}
            </label>
            {renderInput ? (
              <div data-testid={`translation-input-${language}`}>
                {renderInput({
                  language,
                  value: editedTranslations[language] || "",
                  onChange: (value) => handleTranslationChange(language, value),
                  isDefault: language === currentLocale,
                  maxLength,
                })}
              </div>
            ) : multiline ? (
              <textarea
                className="form-control form-control-sm w-full"
                data-testid={`translation-input-${language}`}
                value={editedTranslations[language] || ""}
                onChange={(e) =>
                  handleTranslationChange(language, e.target.value)
                }
                placeholder={I18n.t(
                  "js.shared.multilingualism.translation_popover.placeholder",
                )}
                rows={rows}
                maxLength={maxLength}
              />
            ) : (
              <input
                type="text"
                className="form-control form-control-sm w-full"
                data-testid={`translation-input-${language}`}
                value={editedTranslations[language] || ""}
                onChange={(e) =>
                  handleTranslationChange(language, e.target.value)
                }
                placeholder={I18n.t(
                  "js.shared.multilingualism.translation_popover.placeholder",
                )}
                maxLength={maxLength}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn btn-sm btn-text"
          onClick={handleCancel}
        >
          {I18n.t("js.shared.multilingualism.translation_popover.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          data-testid="translation-save"
          onClick={handleSave}
        >
          {I18n.t("js.shared.multilingualism.translation_popover.save")}
        </button>
      </div>
    </div>
  );
}
